import React, { useEffect } from 'react';
import { isDefined } from 'src/utils/TypeCheckers';
import { environmentService } from 'src/providers/environment/EnvironmentService';
import { guslStorage } from './GuslStorage';
import { SessionContext } from './SessionContext';
import {
  useHttpClient,
  useSessionState,
  useSessionAuth,
  usePermissions,
  useOriginalPath,
} from './hooks';
import { Session, SessionStatus, SignInResponseDTO } from './types';

export const SessionProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  // --- Permissions ---
  const { setPermissions, hasPermission, hasStrictPermission, isProduction } = usePermissions();

  // --- Original path tracking ---
  const { setOriginalPath, getOriginalPath } = useOriginalPath();

  // --- Session observable state (BehaviorSubjects) ---
  const {
    loginSubject,
    systemReadySubject,
    sessionStatusSubject,
    watchLogin,
    watchSystemReady,
    watchSessionStatus,
    getLoggedInUser,
  } = useSessionState();

  // --- HTTP client (needs renewSession — resolved via lazy ref below) ---
  const renewSessionRef = React.useRef<() => Promise<SignInResponseDTO>>(() =>
    Promise.reject('Not initialised'),
  );

  const httpClient = useHttpClient(() => renewSessionRef.current());

  // --- Authentication ---
  const sessionAuth = useSessionAuth({
    httpClient,
    loginSubject,
    systemReadySubject,
    sessionStatusSubject,
    setPermissions,
    setOriginalPath,
  });

  // Wire up the lazy renewSession reference
  renewSessionRef.current = () => {
    const jwtSession = guslStorage.getJwtSession();
    if (!jwtSession) return Promise.reject('No JWT session');
    return sessionAuth.signInWithJwtToken(jwtSession);
  };

  // --- Boot: validate existing session on mount ---
  useEffect(() => {
    systemReadySubject.next(false);

    // Wait for environment to be loaded before validating session.
    const subscription = environmentService.watchEnvironment().subscribe((env) => {
      if (env.loaded) {
        subscription.unsubscribe();
        guslStorage.getSession().then((current: Session) => {
          if (isDefined(current?.jwtTokens)) {
            sessionAuth.performSessionValidation();
          } else {
            sessionStatusSubject.next(SessionStatus.INVALID);
            systemReadySubject.next(true);
          }
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoise so consumers (e.g. useVXMutation's useCallback([sessionContext]))
  // keep a stable reference; a fresh object literal here churns every consumer
  // that depends on the context identity on each provider re-render.
  const contextValue = React.useMemo(
    () => ({
      hasNoSession: sessionAuth.hasNoSession,
      clearSession: guslStorage.clearSession,
      getJwtSession: guslStorage.getJwtSession,
      setJwtSession: guslStorage.setJwtSession,
      isJwt: guslStorage.isJwt,
      preSignIn: sessionAuth.preSignIn,
      signIn: sessionAuth.signIn,
      signIWithNewPassword: sessionAuth.signIWithNewPassword,
      forgotPassword: sessionAuth.forgotPassword,
      getQrCode: sessionAuth.getQrCode,
      verifyTotp: sessionAuth.verifyTotp,
      watchLogin,
      getLoggedInUser,
      watchSystemReady,
      watchSessionStatus,
      signOut: sessionAuth.signOut,
      get: httpClient.get,
      post: httpClient.post,
      put: httpClient.put,
      upload: httpClient.upload,
      _delete: httpClient._delete,
      sendRequest: httpClient.sendRequest,
      setOriginalPath,
      getOriginalPath,
      hasPermission,
      hasStrictPermission,
      isProduction,
    }),
    [
      sessionAuth,
      watchLogin,
      getLoggedInUser,
      watchSystemReady,
      watchSessionStatus,
      httpClient,
      setOriginalPath,
      getOriginalPath,
      hasPermission,
      hasStrictPermission,
      isProduction,
    ],
  );

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
};
